import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown, Quote, ChevronDown, ClipboardList } from "lucide-react";
import { Reveal } from "../components/Reveal";
import GallerySection from "../components/GallerySection";
import SubjectsSection from "../components/SubjectsSection";
import TeamMarquee from "../components/TeamMarquee";
import HomeRecordSearch from "../components/HomeRecordSearch";
import { IMAGES } from "../data/site";
import { useSite } from "../context/SiteContext";

export default function Home() {
  const { settings, courses, slides } = useSite();
  const home = settings.home;
  const college = settings.college;
  const imgs = settings.images || {};
  const forms = settings.forms || {};
  // Hero uses the slide images uploaded in Admin → Home Slides. Multiple slides
  // rotate; a single slide stays static. Falls back to the campus photo if none.
  const heroSlides = (slides && slides.length ? slides : [{ image: "/hero-campus.jpg", caption: "" }]);
  const [idx, setIdx] = useState(0);
  const [applyOpen, setApplyOpen] = useState(false);
  const applyRef = useRef(null);

  useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  useEffect(() => {
    const onClick = (e) => { if (applyRef.current && !applyRef.current.contains(e.target)) setApplyOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO with sliding carousel */}
      <section className="relative h-[62vh] min-h-[500px] flex items-center overflow-hidden" data-testid="hero-carousel">
        <div className="absolute inset-0 bg-forest-900">
          {heroSlides.map((s, i) => (
            <img
              key={s.id || i}
              src={s.image}
              alt={s.caption || "Campus"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-forest-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/30 to-transparent" />

        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.35em] text-gold-light mb-5">
            NEP 2020 · FYUP · {college.location}
          </p>
          <h1 className="font-serif text-bone tracking-tighter leading-[0.9] text-[13vw] md:text-[7.5vw] lg:text-[6rem] drop-shadow-lg">
            <span className="block">Kanggui</span>
            <span className="block italic text-gold-light">Christian</span>
            <span className="block">College</span>
          </h1>
          <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <p className="max-w-md text-bone/90 text-base md:text-lg leading-relaxed drop-shadow">
              {home.heroSubtitle}
            </p>
            <div className="flex items-center gap-3">
              {/* APPLY dropdown */}
              <div className="relative" ref={applyRef}>
                <button
                  data-testid="apply-btn"
                  onClick={() => setApplyOpen((o) => !o)}
                  className="group inline-flex items-center gap-3 bg-gold text-forest px-7 py-4 rounded-md text-sm uppercase tracking-widest font-semibold hover:-translate-y-0.5 transition-transform"
                >
                  <ClipboardList size={18} /> Apply
                  <ChevronDown size={18} className={`transition-transform ${applyOpen ? "rotate-180" : ""}`} />
                </button>
                {applyOpen && (
                  <div data-testid="apply-menu" className="absolute right-0 md:left-0 bottom-full mb-3 bg-bone rounded-md shadow-2xl border border-line py-2 min-w-[220px] z-20">
                    <Link to="/admission" data-testid="apply-admission" className={`block px-5 py-3 text-sm uppercase tracking-widest text-forest hover:bg-line/40 ${forms.admission === false ? "opacity-50" : ""}`}>
                      1. Admission {forms.admission === false && <span className="text-[10px] normal-case">(closed)</span>}
                    </Link>
                    <Link to="/registration" data-testid="apply-registration" className={`block px-5 py-3 text-sm uppercase tracking-widest text-forest hover:bg-line/40 ${forms.registration === false ? "opacity-50" : ""}`}>
                      2. Registration {forms.registration === false && <span className="text-[10px] normal-case">(closed)</span>}
                    </Link>
                    <Link to="/application-status" data-testid="apply-status" className="block px-5 py-3 text-xs uppercase tracking-widest text-muted hover:bg-line/40 border-t border-line mt-1">
                      Check Status / Download
                    </Link>
                  </div>
                )}
              </div>
              <Link
                to="/course"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 bg-bone text-forest px-7 py-4 rounded-md text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform w-fit"
              >
                Programmes
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 right-8 z-10 text-bone/60 hidden md:block animate-bounce">
          <ArrowDown size={22} />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-gold text-forest py-2.5 border-y border-forest/10">
        <Marquee speed={45} gradient={false}>
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i} className="font-serif italic text-lg md:text-xl px-6">
              {home.marquee}&nbsp;<span className="not-italic">✦</span>&nbsp;
            </span>
          ))}
        </Marquee>
      </div>

      <HomeRecordSearch />

      {/* MANIFESTO */}
      <section className="bg-bone py-24 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Our Foundation</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-forest max-w-3xl leading-none">
              {home.manifestoHeading}
            </h2>
          </Reveal>
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
            {home.manifesto.map((m, i) => (
              <Reveal key={m.no || i} delay={i * 0.1}>
                <div className="relative">
                  <span className="font-serif text-7xl md:text-8xl font-light text-line block leading-none mb-4">{m.no}</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-forest mb-4">{m.title}</h3>
                  <p className="text-muted leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPAL */}
      <section className="bg-forest text-bone py-24 md:py-40 overflow-hidden grain relative">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <Reveal className="lg:col-span-5">
              <div className="relative mx-auto w-64 h-64 md:w-80 md:h-80 lg:w-[26rem] lg:h-[26rem] rounded-full overflow-hidden ring-4 ring-gold/40 ring-offset-4 ring-offset-forest shadow-2xl">
                <img src={imgs.principal || IMAGES.principal} alt="Principal" className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-7">
              <Quote size={56} className="text-gold mb-6" />
              <p className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                “{home.principalMessage}”
              </p>
              <div className="mt-10">
                <img src={IMAGES.principalSign} alt="Principal signature" className="h-14 object-contain mb-2 invert opacity-90" />
                <p className="text-xl font-serif text-gold-light">{college.principal}</p>
                <p className="text-sm uppercase tracking-widest text-bone/50 mt-1">Principal, Kanggui Christian College</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ACADEMICS / COURSES PREVIEW */}
      <section className="bg-bone py-24 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Academics</p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-forest leading-none">
                {home.coursesHeading}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/course" data-testid="home-courses-link" className="group inline-flex items-center gap-2 text-forest border-b border-forest pb-1 hover:text-gold hover:border-gold transition-colors">
                View all courses <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {courses.slice(0, 4).map((c, i) => (
              <Reveal key={c.id || c.code || i} delay={i * 0.05}>
                <Link to="/course" className="group block bg-bone p-8 md:p-12 h-full hover:bg-forest transition-colors duration-300">
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-sm text-gold">{c.code}</span>
                    <ArrowUpRight size={20} className="text-forest/40 group-hover:text-bone group-hover:rotate-45 transition-all" />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-forest group-hover:text-bone transition-colors mt-6">{c.title}</h3>
                  <p className="text-muted group-hover:text-bone/60 transition-colors mt-3">{c.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SUBJECTS OPTED */}
      <SubjectsSection />

      {/* TEAM PHOTOS (moving, round) */}
      <TeamMarquee />

      {/* GALLERY */}
      <GallerySection />

      {/* CTA */}
      <section className="relative py-32 md:py-48 overflow-hidden">
        <img src={imgs.gallery || IMAGES.students} alt="Students" className="absolute inset-0 w-full h-full object-cover" />        <div className="absolute inset-0 bg-forest/85" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-7xl text-bone tracking-tight leading-none">
              Begin your journey <span className="italic text-gold-light">with us.</span>
            </h2>
            <p className="text-bone/60 mt-6 max-w-xl mx-auto text-lg">
              Admissions for the FYUP batch are open. Discover a place where learning meets purpose.
            </p>
            <Link to="/student" data-testid="cta-admissions" className="mt-10 inline-flex items-center gap-3 bg-gold text-forest px-8 py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
              Student Life & Admissions <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
